import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';

import { useChangeUserData, useGetUser } from '@/api';
import { ChangeUserDataBody } from '@/api/types';
import { RequestCodeIcon } from '@/components/icons/modal';
import { Loader } from '@/components/Loader/Loader.tsx';
import { PersonalDataSchema } from '@/components/Profile/Settings/constants.ts';
import { Button } from '@/components/shared/Button';
import { Input } from '@/components/shared/Input/Input.tsx';
import { PageHeading } from '@/components/shared/PageHeading/PageHeading.tsx';
import { numberMask, numberPlaceholder, strFormatChars, strMask } from '@/constants';
import { useTimer } from '@/hooks';
import { formatNumber, formatNumberToFullStr, getRemainingSeconds } from '@/utils';

import cn from './styles.module.sass';

type PersonalData = {
    firstname: string;
    lastname: string;
    msisdn: string;
    email: string;
    code: string;
};

export const Settings = () => {
    const { data: userData } = useGetUser();
    const { mutateAsync: changeData, isPending: isChangeDataPending } = useChangeUserData();

    const [isCodeRequested, setIsCodeRequested] = useState(false);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [isNumberDifferent, setIsNumberDifferent] = useState(false);

    const { control, clearErrors, setValue, setError, getFieldState, handleSubmit, getValues } =
        useForm<PersonalData>({
            defaultValues: {
                firstname: userData ? userData.firstname : '',
                lastname: userData ? userData.lastname : '',
                msisdn: userData ? formatNumberToFullStr(userData?.msisdn) : '',
                email: userData ? (userData.email ?? '') : '',
                code: '',
            },
            resolver: zodResolver(PersonalDataSchema),
            mode: 'all',
        });

    const isValidCode = getValues('code').length === 6;

    const remainingSeconds = useMemo(
        () =>
            getRemainingSeconds(
                localStorage.getItem('timestamp') || Math.ceil(Date.now() + 1000 * 60)
            ),
        [isChangeDataPending]
    );

    const { time, start, reset } = useTimer(remainingSeconds);

    const onCodeRequest = async () => {
        const { msisdn, email } = getValues();

        const isFormValid = !getFieldState('msisdn').invalid && !getFieldState('email').invalid;

        const isDifferentData = userData
            ? isNumberDifferent || email !== (userData.email === undefined ? '' : userData.email)
            : false;

        if (isFormValid && isDifferentData) {
            const formattedNumber = formatNumber(msisdn);
            const reqBody: ChangeUserDataBody = {
                msisdn: formattedNumber,
                email: email,
            };

            await changeData(reqBody);

            if (userData && (userData.email || isNumberDifferent)) {
                start();
                setIsTimerActive(true);
                setIsCodeRequested(true);
            }
        }
    };

    const onSubmit = async (data: PersonalData) => {
        const { msisdn, email } = getValues();

        const isFormValid = !getFieldState('msisdn').invalid && !getFieldState('email').invalid;

        const isDifferentData = userData
            ? formatNumberToFullStr(userData?.msisdn) !== msisdn || email !== userData.email
            : false;

        if (isValidCode && isFormValid && isDifferentData) {
            const formattedNumber = formatNumber(data.msisdn);
            const reqBody: ChangeUserDataBody = {
                msisdn: formattedNumber,
                email: data.email,
                code: getValues('code'),
            };

            const errors = await changeData(reqBody);
            const incorrectCode = errors?.find(error => error.type === 'incorrect_password_code');

            if (incorrectCode) {
                setError('code', { message: 'Неверный код' });
            } else {
                setIsTimerActive(false);
                setIsCodeRequested(false);
                setValue('code', '');
            }
        }
    };

    const AfterCodeReqBtn = isChangeDataPending ? (
        <div className={cn.loaderContainer}>
            <Loader mini />
        </div>
    ) : (
        <Button
            disabled={isTimerActive}
            onClick={onCodeRequest}
            background="transparent"
            className={clsx(cn.requestCodeBtnAgain, isTimerActive && cn.grey)}
        >
            {!isTimerActive && <span>{RequestCodeIcon} </span>}
            Запросить код повторно
            {isTimerActive && ` через ${time} сек`}
        </Button>
    );

    useEffect(() => {
        if (time <= 0) {
            setIsTimerActive(false);
            localStorage.removeItem('timestamp');
            reset();
        }
    }, [reset, time]);

    useEffect(() => {
        if (userData) {
            setValue('firstname', userData.firstname);
            setValue('lastname', userData.lastname);
            setValue('msisdn', formatNumberToFullStr(userData?.msisdn));
            setValue('email', userData.email ?? '');
        }
    }, [userData]);

    return (
        <div className={cn.container}>
            <PageHeading pageName="Настройки профиля" />
            <div className={cn.personalData}>
                <h3>Личные данные</h3>
                <form onSubmit={handleSubmit(isCodeRequested ? onSubmit : onCodeRequest)}>
                    <div className={cn.inputs}>
                        <Controller
                            render={({ field: { value, onChange, onBlur } }) => (
                                <Input
                                    mask={strMask}
                                    formatChars={strFormatChars}
                                    maskChar=""
                                    label="Имя*"
                                    value={value}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    hasClearBtn={false}
                                    disabled
                                />
                            )}
                            name="firstname"
                            control={control}
                        />
                        <Controller
                            render={({ field: { value, onChange, onBlur } }) => (
                                <Input
                                    mask={strMask}
                                    formatChars={strFormatChars}
                                    maskChar=""
                                    label="Фамилия*"
                                    value={value}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    hasClearBtn={false}
                                    disabled
                                />
                            )}
                            name="lastname"
                            control={control}
                        />
                        <div className={cn.phoneInputContainer}>
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur },
                                    fieldState: { invalid },
                                }) => (
                                    <Input
                                        mask={numberMask}
                                        placeholder={numberPlaceholder}
                                        type="tel"
                                        label="Номер телефона*"
                                        value={value}
                                        onBlur={onBlur}
                                        onChange={e => {
                                            onChange(e);

                                            if (userData) {
                                                setIsNumberDifferent(
                                                    formatNumberToFullStr(userData.msisdn) !==
                                                        e.target.value
                                                );
                                            }
                                        }}
                                        hasClearBtn={false}
                                        error={invalid}
                                    />
                                )}
                                name="msisdn"
                                control={control}
                            />
                            <p className={cn.phoneHelperText}>
                                Можно менять не чаще 1 раза в 3 месяца
                            </p>
                        </div>

                        <Controller
                            render={({
                                field: { value, onChange, onBlur },
                                fieldState: { invalid },
                            }) => (
                                <Input
                                    type="email"
                                    label="E-mail"
                                    value={value}
                                    onBlur={onBlur}
                                    onChange={onChange}
                                    error={invalid}
                                    hasClearBtn
                                    onClear={e => {
                                        e.preventDefault();
                                        setValue('email', '');
                                    }}
                                />
                            )}
                            name="email"
                            control={control}
                        />
                        {isCodeRequested && (
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur },
                                    fieldState: { error },
                                }) => (
                                    <Input
                                        type="tel"
                                        label="Код подтверждения*"
                                        mask="999999"
                                        maskChar=""
                                        value={value}
                                        onBlur={onBlur}
                                        onChange={e => {
                                            clearErrors('code');
                                            onChange(e);
                                        }}
                                        error={
                                            (value.length !== 6 && value.length !== 0) ||
                                            !!error?.message
                                        }
                                        helperText={error?.message}
                                        hasClearBtn
                                        onClear={e => {
                                            e.preventDefault();
                                            setValue('code', '');
                                        }}
                                    />
                                )}
                                name="code"
                                control={control}
                            />
                        )}
                    </div>
                    <div className={cn.btnsContainer}>
                        {(isTimerActive || isCodeRequested) && AfterCodeReqBtn}
                        {isChangeDataPending ? (
                            <Skeleton height={48} />
                        ) : (
                            <Button type="submit" className={cn.submitBtn}>
                                {isCodeRequested ||
                                (userData && !userData.email && !isNumberDifferent)
                                    ? 'Сохранить изменения'
                                    : 'Подтвердить изменения'}
                            </Button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};
